// Builder for TemplateContext from domain objects.
//
// Pure, synchronous, and has no runtime consumers yet -- the Templates
// service layer and the send-dialog wiring will import it when they land.
// The function returns a context object suitable for passing directly to
// interpolateTemplate() / renderTemplatePreview().
//
// Design principles:
// - Pure and sync. No I/O, no new Date() by default, no auth lookups.
//   The caller is responsible for pre-fetching the agent profile via
//   fetchLocatorProfile() (see services/emailService.ts) and for
//   sorting selectedProperties into the order it wants featured.
// - Optional-everywhere. Every output key is optional; missing inputs
//   yield an absent key, and interpolateTemplate's fail-soft rule takes
//   over ("{{missing_key}}" -> ""). This keeps the function safe to
//   call with partial data during preview or stub states.
// - Structural input types. We do not import Client / LocatorProfile /
//   GroupedProperty directly, to keep this utility free of coupling to
//   DB / service layers. Real domain types are assignable to the
//   interfaces below because they share the needed fields.
//
// Decision log (see commit history for rationale):
// 1. client_full_name = trim(`${first_name} ${last_name}`). No full_name
//    column exists on the clients table.
// 2. Agent identity is sourced from the profiles table via
//    fetchLocatorProfile() -- NOT from supabase.auth.getUser() metadata.
//    Caller fetches, we map. agent_first_name is derived as the first
//    whitespace-delimited token of full_name.
// 3. property_count is the count of properties passed in. If a future
//    template needs floor-plan-level granularity, add a separate key
//    (e.g. floor_plan_count) rather than overloading this one.
// 4. first_property_name is selectedProperties[0].property_name.
//    Sort before calling if a non-selection-order "first" is needed.
// 5. budget_range is override-first: if the caller passes
//    systemFields.budget_range (e.g. derived from the active
//    search/listing context which is closer to send-time intent
//    than the client profile), that value wins. Otherwise we fall
//    back to deriving from client.min_budget/max_budget using the
//    existing savedSearchSummary pattern ("$1,800 - $2,400" /
//    "From $X" / "Up to $X"). Formatter is duplicated here
//    intentionally to avoid touching working code in
//    ApartmentDashboard; future refactor can extract a shared helper.
// 6. today_date is caller-provided via systemFields. We do NOT call
//    new Date() so tests stay deterministic and callers pick TZ/format.
// 7. unsubscribe_link is caller-provided, with a safe default of
//    `mailto:${agent.email}?subject=Unsubscribe` matching the existing
//    footer pattern in the listing email template. Omitted entirely
//    when neither an override nor an agent email is available.

import type { TemplateContext } from "./templateInterpolation";

/**
 * Minimal client shape required for context building.
 * Real `Client` from types/index.ts is assignable.
 */
export interface TemplateContextClient {
  first_name?: string | null;
  last_name?: string | null;
  min_budget?: number | null;
  max_budget?: number | null;
}

/**
 * Minimal agent/profile shape. `LocatorProfile` from emailService.ts is
 * assignable. Caller fetches via fetchLocatorProfile() before calling.
 */
export interface TemplateContextAgent {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

/**
 * Minimal property shape. `GroupedProperty` from emailService.ts is
 * assignable. Ordering is the caller's responsibility.
 */
export interface TemplateContextProperty {
  property_name?: string | null;
}

/**
 * Fields the builder will not compute on its own -- pass them in.
 * Keeping these out of the arg list prevents the function from
 * acquiring clock/tracking dependencies.
 */
export interface TemplateContextSystemFields {
  /**
   * Pre-formatted date string for {{today_date}}. Omit to leave the
   * key unset (placeholder renders as ""). Typical value:
   *   new Date().toLocaleDateString("en-US", {
   *     year: "numeric", month: "long", day: "numeric"
   *   })
   */
  today_date?: string;

  /**
   * Fully-built unsubscribe URL for {{unsubscribe_link}}. If omitted,
   * defaults to `mailto:${agent.email}?subject=Unsubscribe` when an
   * agent email is present. Omitted entirely otherwise.
   */
  unsubscribe_link?: string;

  /**
   * Pre-formatted budget range string for {{budget_range}}. Takes
   * precedence over any value derived from client.min_budget /
   * max_budget.
   *
   * Pass this when the send context has a better budget source than
   * the client profile -- e.g. the budget the user filtered the
   * active search on, or an agreed-budget attached to the selected
   * listings. Falls back to the client profile only when omitted.
   */
  budget_range?: string;
}

/**
 * Build a TemplateContext from domain objects.
 *
 * All arguments except the return are treated as read-only. Any argument
 * may be null/undefined -- missing data produces an absent output key,
 * and interpolation handles that as empty string.
 */
export function buildTemplateContext(
  client: TemplateContextClient | null | undefined,
  agent: TemplateContextAgent | null | undefined,
  selectedProperties: ReadonlyArray<TemplateContextProperty> = [],
  systemFields: TemplateContextSystemFields = {}
): TemplateContext {
  const ctx: TemplateContext = {};

  // --- Client fields -----------------------------------------------------
  if (client) {
    const firstName = nonEmpty(client.first_name);
    const lastName = nonEmpty(client.last_name);
    if (firstName) ctx.client_first_name = firstName;
    if (lastName) ctx.client_last_name = lastName;
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    if (fullName) ctx.client_full_name = fullName;
  }

  // --- Budget range (override-first) ------------------------------------
  // Prefer an explicit systemFields override so callers can pass the
  // budget the user actually filtered the current search on, rather
  // than being stuck with the potentially-stale client profile value.
  const budgetOverride = nonEmpty(systemFields.budget_range);
  if (budgetOverride) {
    ctx.budget_range = budgetOverride;
  } else if (client) {
    const derived = formatBudgetRange(client.min_budget, client.max_budget);
    if (derived) ctx.budget_range = derived;
  }

  // --- Agent fields ------------------------------------------------------
  if (agent) {
    const agentFullName = nonEmpty(agent.full_name);
    const agentEmail = nonEmpty(agent.email);
    const agentPhone = nonEmpty(agent.phone);
    if (agentFullName) {
      ctx.agent_full_name = agentFullName;
      const firstToken = agentFullName.split(/\s+/)[0];
      if (firstToken) ctx.agent_first_name = firstToken;
    }
    if (agentEmail) ctx.agent_email = agentEmail;
    if (agentPhone) ctx.agent_phone = agentPhone;
  }

  // --- Selection / property fields --------------------------------------
  ctx.property_count = selectedProperties.length;
  const firstPropertyName = nonEmpty(selectedProperties[0]?.property_name);
  if (firstPropertyName) ctx.first_property_name = firstPropertyName;

  // --- System fields -----------------------------------------------------
  const today = nonEmpty(systemFields.today_date);
  if (today) ctx.today_date = today;

  const unsubscribeOverride = nonEmpty(systemFields.unsubscribe_link);
  if (unsubscribeOverride) {
    ctx.unsubscribe_link = unsubscribeOverride;
  } else if (ctx.agent_email) {
    ctx.unsubscribe_link = `mailto:${ctx.agent_email}?subject=Unsubscribe`;
  }

  return ctx;
}

function nonEmpty(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Format a min/max budget pair using the project's established currency
 * style (USD, no decimals, comma thousands). Mirrors the pattern used in
 * buildSavedSearchSummary (ApartmentDashboard.tsx). Returns undefined
 * when neither bound is a finite number so the key drops out of context.
 *
 * NOTE: duplicated formatter to avoid touching working code. A future
 * refactor can extract a shared helper once the template feature lands
 * and has its own consumers of this logic.
 */
function formatBudgetRange(
  min: number | null | undefined,
  max: number | null | undefined
): string | undefined {
  const hasMin = typeof min === "number" && Number.isFinite(min);
  const hasMax = typeof max === "number" && Number.isFinite(max);
  if (!hasMin && !hasMax) return undefined;

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  if (hasMin && hasMax) return `${fmt(min as number)} - ${fmt(max as number)}`;
  if (hasMin) return `From ${fmt(min as number)}`;
  return `Up to ${fmt(max as number)}`;
}
